import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { hole } from '../lib/api'
import { datumHeute, datumKurz, instanzSchluessel, zeitKurz } from '../lib/datum'
import type { MitgliedKurz } from '../lib/modelle'
import { SLOT_NAME, istSlot } from '../lib/mahlzeiten'
import { zeitSpanne } from '../lib/stundenplan'
import { mitgliedsFarbe } from '../session/logic'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { Avatar, Button, Card, Empty, ListItem } from '../ui'

interface Widget {
  type: string
  priority: number
  payload: unknown
}

interface TermineNutzlast {
  eintraege: {
    eventId: string
    title: string
    startsAt: string | null
    endsAt: string | null
    allDayOn: string | null
    location: string | null
    teilnehmer: string[]
  }[]
}

interface MahlzeitenNutzlast {
  eintraege: {
    id: string
    slot: string
    title: string
    recipeId: string | null
    servings: number | null
  }[]
}

interface SchuleNutzlast {
  plaene: {
    timetableId: string
    memberId: string
    name: string
    week: string | null
    lessons: {
      id: string
      startsAt: string
      endsAt: string
      subject: { name: string } | null
      room: string | null
    }[]
  }[]
}

interface AufgabenNutzlast {
  eintraege: {
    taskId: string
    title: string
    kind: string
    priority: number
    occurrenceDate: string | null
    ueberfaellig: boolean
    zustaendig: string[]
  }[]
}

export function Heute() {
  const { abfrage } = useSitzung()
  const darfVerwalten = useDarf('member.manage')
  const datum = datumHeute()

  const dash = useQuery({
    queryKey: ['dashboard', datum],
    queryFn: () => hole<{ widgets: Widget[] }>(`/api/dashboard?date=${datum}`),
  })
  const mitglieder = useQuery({
    queryKey: ['members'],
    queryFn: () => hole<MitgliedKurz[]>('/api/members'),
  })

  const heute = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
  const name = abfrage.data && !abfrage.data.setupRequired ? abfrage.data.member.displayName : ''
  const zeitzone =
    abfrage.data && !abfrage.data.setupRequired ? abfrage.data.household.timezone : undefined
  const personen = new Map((mitglieder.data ?? []).map((m) => [m.id, m]))

  const farbeVon = (ids: string[]): string | undefined => {
    const erste = ids[0]
    const m = erste ? personen.get(erste) : undefined
    return m ? mitgliedsFarbe(m.color, m.id) : undefined
  }
  const personVon = (ids: string[]) => {
    const erste = ids[0]
    return erste ? personen.get(erste) : undefined
  }
  const namen = (ids: string[]): string =>
    ids
      .map((id) => personen.get(id)?.displayName)
      .filter(Boolean)
      .join(', ')

  const widgets = dash.data?.widgets ?? []
  // ⚠️ Ein unbekannter Typ wird ÜBERSPRUNGEN, nicht als Schlüssel angezeigt.
  // Vorher stand auf dem Dashboard wörtlich „tasks.offen".
  const termine = widgets.find((w) => w.type === 'calendar.heute')?.payload as
    TermineNutzlast | undefined
  const mahlzeiten = widgets.find((w) => w.type === 'meals.heute')?.payload as
    MahlzeitenNutzlast | undefined
  const aufgaben = widgets.find((w) => w.type === 'tasks.offen')?.payload as
    AufgabenNutzlast | undefined
  const schule = widgets.find((w) => w.type === 'timetable.heute')?.payload as
    SchuleNutzlast | undefined

  const hatInhalt =
    (termine?.eintraege.length ?? 0) > 0 ||
    (aufgaben?.eintraege.length ?? 0) > 0 ||
    (mahlzeiten?.eintraege.length ?? 0) > 0 ||
    (schule?.plaene.length ?? 0) > 0

  if (dash.isError)
    return (
      <p className="fehler" role="alert">
        Die Daten konnten nicht geladen werden.{' '}
        <Button
          variant="text"
          onClick={() => {
            void dash.refetch()
          }}
        >
          Erneut versuchen
        </Button>
      </p>
    )

  return (
    <>
      <div>
        <h1>{heute}</h1>
        <p className="unterzeile">Guten Tag, {name}.</p>
      </div>

      {dash.isPending ? <p className="unterzeile">Wird geladen …</p> : null}

      {termine ? (
        <Card
          titel="Heute im Kalender"
          aktion={
            <Link className="btn btn--text" to="/kalender">
              <span className="btn__text">Kalender</span>
            </Link>
          }
        >
          {termine.eintraege.length === 0 ? (
            <p className="unterzeile">Keine Termine heute.</p>
          ) : (
            <ul className="liste liste--gruppe">
              {termine.eintraege.map((t) => {
                const von = zeitKurz(t.startsAt, zeitzone)
                const bis = zeitKurz(t.endsAt, zeitzone)
                const neben = [
                  t.allDayOn ? 'Ganztägig' : von && bis ? `${von} – ${bis}` : von,
                  t.location,
                  namen(t.teilnehmer) || null,
                ]
                  .filter(Boolean)
                  .join(' · ')
                const wer = personVon(t.teilnehmer)
                return (
                  <ListItem
                    key={`${t.eventId}-${t.startsAt ?? 'ganztag'}`}
                    titel={t.title}
                    unterzeile={neben}
                    farbe={farbeVon(t.teilnehmer)}
                    fuehrend={
                      wer ? (
                        <Avatar name={wer.displayName} farbe={mitgliedsFarbe(wer.color, wer.id)} />
                      ) : undefined
                    }
                  />
                )
              })}
            </ul>
          )}
        </Card>
      ) : null}

      {schule && schule.plaene.length > 0 ? (
        <Card
          titel="Heute Schule"
          aktion={
            <Link className="btn btn--text" to="/stundenplan">
              <span className="btn__text">Stundenplan</span>
            </Link>
          }
        >
          {schule.plaene.map((p) => {
            const wer = personen.get(p.memberId)
            return (
              <div className="stack-2" key={p.timetableId}>
                <h3 className="abschnitt-titel">
                  {wer?.displayName ?? p.name}
                  {p.week ? ` · Woche ${p.week}` : ''}
                </h3>
                <ul className="liste liste--gruppe">
                  {p.lessons.map((s) => (
                    <ListItem
                      key={s.id}
                      titel={s.subject?.name ?? 'Unterricht'}
                      unterzeile={`${zeitSpanne(s)}${s.room ? ` · ${s.room}` : ''}`}
                      farbe={farbeVon([p.memberId])}
                    />
                  ))}
                </ul>
              </div>
            )
          })}
        </Card>
      ) : null}

      {mahlzeiten ? (
        <Card
          titel="Heute zu essen"
          aktion={
            <Link className="btn btn--text" to="/mahlzeiten">
              <span className="btn__text">Mahlzeiten</span>
            </Link>
          }
        >
          {mahlzeiten.eintraege.length === 0 ? (
            <p className="unterzeile">Nichts geplant.</p>
          ) : (
            <ul className="liste liste--gruppe">
              {mahlzeiten.eintraege.map((m) => (
                <ListItem
                  key={m.id}
                  titel={m.title}
                  unterzeile={istSlot(m.slot) ? SLOT_NAME[m.slot] : m.slot}
                />
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {aufgaben ? (
        <Card
          titel="Offene Aufgaben"
          aktion={
            <Link className="btn btn--text" to="/aufgaben">
              <span className="btn__text">Aufgaben</span>
            </Link>
          }
        >
          {aufgaben.eintraege.length === 0 ? (
            <p className="unterzeile">Nichts offen. Schön.</p>
          ) : (
            <ul className="liste liste--gruppe">
              {aufgaben.eintraege.map((a) => {
                const neben = [
                  a.ueberfaellig ? 'Überfällig' : null,
                  a.occurrenceDate ? datumKurz(a.occurrenceDate) : 'Irgendwann',
                  a.kind === 'chore' ? 'Haushalt' : null,
                  namen(a.zustaendig) || null,
                ]
                  .filter(Boolean)
                  .join(' · ')
                const wer = personVon(a.zustaendig)
                return (
                  <ListItem
                    key={instanzSchluessel(a.taskId, a.occurrenceDate)}
                    titel={a.title}
                    unterzeile={neben}
                    farbe={farbeVon(a.zustaendig)}
                    fuehrend={
                      wer ? (
                        <Avatar name={wer.displayName} farbe={mitgliedsFarbe(wer.color, wer.id)} />
                      ) : undefined
                    }
                    folgend={
                      a.priority > 0 ? (
                        <span className="dringlich" aria-label={`Priorität ${a.priority}`}>
                          {'!'.repeat(a.priority)}
                        </span>
                      ) : undefined
                    }
                  />
                )
              })}
            </ul>
          )}
        </Card>
      ) : null}

      {!dash.isPending && widgets.length === 0 ? (
        <Empty
          titel="Hier ist noch nichts"
          text="Famora zeigt an dieser Stelle, was heute ansteht. Für dich ist noch kein Bereich freigeschaltet."
          aktion={
            darfVerwalten ? (
              <Link className="btn btn--filled" to="/familie">
                <span className="btn__text">Familie einrichten</span>
              </Link>
            ) : undefined
          }
        />
      ) : null}

      {!dash.isPending && widgets.length > 0 && !hatInhalt ? (
        <p className="unterzeile">Für heute ist nichts eingetragen.</p>
      ) : null}
    </>
  )
}
